'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { ProviderIcon } from '@/components/provider-icon';
import { Spinner } from '@/components/ui/spinner';
import {
  buildIntegrationCatalog,
  findTargetById,
  resolveDeeplinkTarget,
  type ConnectedAccountSummary,
  type FeaturedMcpEntry,
  type IntegrationStatus,
  type IntegrationTarget,
  type McpSkillEntry,
  type OpenApiSkillEntry,
  type PlatformIntegrationRow,
} from './integration-catalog';
import {
  parseIntegrationDeeplink,
  stripIntegrationDeeplink,
  type IntegrationDeeplink,
} from './use-integration-deeplink';
import {
  dispatchIntegrationChanged,
  useIntegrationChanged,
} from './integration-events';
import { useAuth } from '@/components/auth-provider';
import { IntegrationOAuthClientPanel } from './integration-oauth-client-panel';
import { FeaturedMcpConnectPanel } from './featured-mcp-connect-panel';
import { startFeaturedMcpOauthFlow } from './oauth-launcher';
import {
  parseHubParams,
  type HubMode,
  type ParsedHubParams,
} from './parse-hub-params';
import {
  ToolPolicyEditor,
  type ToolPolicyItem,
} from './tool-policy-editor';
import { normalizeToolPolicies, type ToolPolicyConfig, type ToolPolicyValue } from '@/lib/agents/tool-policies';
import { buildDiscordInstallUrl } from '@/lib/messaging/discord-install-url';
import { useT } from '@/components/i18n-provider';

interface IntegrationHubProps {
  agentConfigId: string;
  agentUserId: string;
  agentUsername: string;
  open: boolean;
  onClose: () => void;
  /**
   * Called whenever the hub completes a mutation that may have changed
   * the agent's tool inventory or integration list. The parent should
   * use this to re-derive state (monetization rows, summary cards) so
   * we never need a full page reload after a hub action.
   */
  onChanged?: () => void;
  /**
   * Optional initial deep-link envelope. If omitted, the hub parses the
   * URL itself.
   */
  initialDeeplink?: IntegrationDeeplink | null;
  initialFocus?: ParsedHubParams | null;
}

const STATUS_LABEL: Record<IntegrationStatus, string> = {
  connected: 'Connected',
  available: 'Available',
  needs_account: 'Needs account',
  needs_tool_selection: 'Needs tool selection',
  reconnect_required: 'Reconnect required',
  unsupported: 'Unsupported',
};

function ToggleIndicator({ value }: { value: boolean }) {
  const t = useT();
  return (
    <span
      aria-label={value ? t('integrations.enabled') : t('agentTools.disabled')}
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
        value ? 'bg-emerald-600/90 ring-1 ring-emerald-400/40' : 'bg-muted-foreground/30 ring-1 ring-border'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          value ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

function isTargetConnected(target: IntegrationTarget): boolean {
  // The toggle shows the user's stored intent (row.isEnabled), not the derived
  // "is the credential currently healthy" state. Otherwise a row that's enabled
  // in the DB but missing a credential renders as OFF, hides the fact that the
  // agent runtime would still try to load it, and makes the toolcall failures
  // ("sendgrid_credential_not_found") look unexplained.
  if (target.kind === 'custom_mcp' || target.kind === 'featured_mcp') {
    return !!target.mcpConfig && target.mcpConfig.enabled !== false;
  }
  if (target.kind === 'openapi') {
    return !!target.openapiConfig && target.openapiConfig.enabled !== false;
  }
  // Platform / messaging / media-gen targets: rely on the integration row.
  // `connected` already implies isEnabled; the other "needs_*" statuses
  // also mean the row is enabled but sub-state is incomplete — still ON
  // from the owner's perspective.
  if (target.integrationRowId) {
    return (
      target.status === 'connected' ||
      target.status === 'needs_account' ||
      target.status === 'needs_tool_selection' ||
      target.status === 'reconnect_required'
    );
  }
  return target.status === 'connected';
}

function isAddTarget(target: IntegrationTarget): boolean {
  return target.kind === 'custom_mcp_add' || target.kind === 'custom_openapi_add';
}

/**
 * Agent Integration Hub — the canonical surface for managing what an
 * agent can actually do. Replaces the sprawling inline section in
 * `AgentIntegrations` with a compact management dialog.
 *
 * Design notes:
 *   - The hub is the **single owner of integration data refresh**.
 *     Any mutation routes through `refreshIntegrationState()`, never
 *     `window.location.reload()`.
 *   - Detail panels delegate to the existing connect/save endpoints.
 *     For now, complex configuration flows (capability checkboxes,
 *     messaging binding, media-gen model selection, full MCP probe
 *     flows) keep working through the inline `AgentIntegrations`
 *     component below — the hub provides clear "Configure" CTAs that
 *     scroll to the relevant section. Phase 2 of the rollout migrates
 *     those flows in-place.
 *   - All deep-links route through `useIntegrationDeeplink` so factory
 *     `buildSetupDeepLink` URLs land here predictably.
 */
export function IntegrationHub({
  agentConfigId,
  agentUserId,
  agentUsername,
  open,
  onClose,
  onChanged,
  initialDeeplink = null,
  initialFocus = null,
}: IntegrationHubProps) {
  const t = useT();
  const { connectedAccounts: humanAccounts } = useAuth();

  const [loading, setLoading] = useState(true);
  const [agentRows, setAgentRows] = useState<PlatformIntegrationRow[]>([]);
  const [mcpSkills, setMcpSkills] = useState<Record<string, McpSkillEntry>>({});
  const [openapiSkills, setOpenapiSkills] = useState<Record<string, OpenApiSkillEntry>>(
    {},
  );
  const [featuredMcps, setFeaturedMcps] = useState<FeaturedMcpEntry[]>([]);
  const [agentConnectedAccounts, setAgentConnectedAccounts] = useState<
    ConnectedAccountSummary[]
  >([]);

  const [query, setQuery] = useState(initialDeeplink?.integrationQuery ?? '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<HubMode | null>(
    initialFocus?.mode ?? null,
  );
  const [toast, setToast] = useState<IntegrationDeeplink['toast']>(
    initialDeeplink?.toast ?? null,
  );
  const [wideLayout, setWideLayout] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const consumedDeeplinkRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && initialFocus) consumedDeeplinkRef.current = false;
  }, [open, initialFocus]);

  useEffect(() => {
    if (open && !wasOpenRef.current && !wideLayout && !initialFocus?.open) {
      setSelectedId(null);
    }
    wasOpenRef.current = open;
  }, [initialFocus?.open, open, wideLayout]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 768px)');
    const sync = () => setWideLayout(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  const refresh = useCallback(async () => {
    if (!open) return;
    try {
      const [intRes, agentRes, catRes, accRes] = await Promise.all([
        fetch(`/api/agents/${agentConfigId}/integrations`, { cache: 'no-store' }),
        fetch(`/api/agents/${agentUserId}`, { cache: 'no-store' }),
        fetch('/api/mcp/catalog', { cache: 'no-store' }),
        fetch(`/api/connected-accounts?forAgent=${encodeURIComponent(agentUserId)}`, { cache: 'no-store' }).catch(
          () => null,
        ),
      ]);

      if (intRes.ok) {
        const intJson = await intRes.json();
        setAgentRows((intJson.integrations as PlatformIntegrationRow[]) ?? []);
      }
      if (agentRes.ok) {
        const agentJson = await agentRes.json();
        const skills = agentJson?.config?.skills;
        if (skills && typeof skills === 'object') {
          setMcpSkills(((skills as Record<string, unknown>).mcp as Record<string, McpSkillEntry>) ?? {});
          setOpenapiSkills(
            ((skills as Record<string, unknown>).openapi as Record<string, OpenApiSkillEntry>) ?? {},
          );
        }
      }
      if (catRes.ok) {
        const catJson = await catRes.json();
        setFeaturedMcps((catJson.items as FeaturedMcpEntry[]) ?? []);
      }
      if (accRes && accRes.ok) {
        const accJson = await accRes.json();
        const items = (accJson.accounts as Array<{ id: string; provider: string; userId?: string }>)
          ?? [];
        setAgentConnectedAccounts(
          items.map((a) => ({
            id: a.id,
            provider: a.provider,
            isAgentOwned: a.userId === agentUserId,
          })),
        );
      } else {
        // Fallback: assume any `mcp:*` from the human auth list is NOT an
        // agent-owned credential (it isn't — they're all human rows). The
        // hub still works without this endpoint; OAuth connected status
        // for MCP is also reported by the integrations endpoint.
        setAgentConnectedAccounts([]);
      }
    } finally {
      setLoading(false);
      setRefreshVersion((value) => value + 1);
    }
  }, [agentConfigId, agentUserId, open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    refresh();
  }, [open, refresh]);

  // Identity-only providers the human user has tokens for.
  const humanIdentityProviderIds = useMemo(() => {
    const set = new Set<string>();
    for (const acc of humanAccounts) set.add(acc.provider);
    return set;
  }, [humanAccounts]);

  // Enrich agent connected accounts using the integrations response,
  // which signals MCP OAuth connected state via `mcpOauthConnected`.
  const enrichedAccounts = useMemo<ConnectedAccountSummary[]>(() => {
    const merged: ConnectedAccountSummary[] = [...agentConnectedAccounts];
    for (const row of agentRows) {
      if (row.type === 'custom_mcp' && row.name && row.mcpOauthConnected) {
        merged.push({
          id: `synthetic:mcp:${row.name}`,
          provider: `mcp:${row.name}`,
          isAgentOwned: true,
        });
      }
    }
    return merged;
  }, [agentConnectedAccounts, agentRows]);

  const catalog = useMemo(() => {
    return buildIntegrationCatalog({
      agentIntegrations: agentRows,
      mcpSkills,
      openapiSkills,
      featuredMcps,
      connectedAccounts: enrichedAccounts,
      humanIdentityProviderIds,
    });
  }, [
    agentRows,
    mcpSkills,
    openapiSkills,
    featuredMcps,
    enrichedAccounts,
    humanIdentityProviderIds,
  ]);

  const applyLocalDisable = useCallback((target: IntegrationTarget) => {
    if (target.kind === 'featured_mcp' || target.kind === 'custom_mcp') {
      const serverName = target.modeRefs?.mcp_hosted ?? target.refId;
      setMcpSkills((current) => {
        const existing = current[serverName] ?? target.mcpConfig;
        if (!existing) return current;
        return { ...current, [serverName]: { ...existing, enabled: false } };
      });
      return;
    }
    if (target.kind === 'openapi') {
      const serverName = target.modeRefs?.openapi ?? target.refId;
      setOpenapiSkills((current) => {
        const existing = current[serverName] ?? target.openapiConfig;
        if (!existing) return current;
        return { ...current, [serverName]: { ...existing, enabled: false } };
      });
      return;
    }
    if (target.integrationRowId) {
      setAgentRows((current) =>
        current.map((row) =>
          row.id === target.integrationRowId
            ? { ...row, isEnabled: false }
            : row,
        ),
      );
    }
  }, []);

  const applyLocalDelete = useCallback((target: IntegrationTarget) => {
    if (target.kind === 'custom_mcp') {
      const serverName = target.modeRefs?.mcp_hosted ?? target.refId;
      setMcpSkills((current) => {
        const next = { ...current };
        delete next[serverName];
        return next;
      });
      setSelectedId(null);
      setAgentConnectedAccounts((current) => current.filter((account) => account.provider !== `mcp:${serverName}`));
      return;
    }
    if (target.kind === 'openapi') {
      const serverName = target.modeRefs?.openapi ?? target.refId;
      setOpenapiSkills((current) => {
        const next = { ...current };
        delete next[serverName];
        return next;
      });
      setSelectedId(null);
      setAgentConnectedAccounts((current) => current.filter((account) => account.provider !== `openapi:${serverName}`));
      return;
    }
    if (target.integrationRowId) {
      setAgentRows((current) => current.filter((row) => row.id !== target.integrationRowId));
      if (target.connectedAccount?.id) {
        setAgentConnectedAccounts((current) => current.filter((account) => account.id !== target.connectedAccount?.id));
      }
      setSelectedId(null);
    }
  }, []);

  const filtered = catalog;

  useEffect(() => {
    if (!open || loading) return;
    if (!wideLayout && !selectedId) return;
    if (selectedId && filtered.some((target) => target.id === selectedId)) return;
    setSelectedId(filtered[0]?.id ?? null);
  }, [filtered, loading, open, selectedId, wideLayout]);

  // Apply deeplink focus once data is loaded.
  useEffect(() => {
    if (!open) return;
    if (loading) return;
    if (consumedDeeplinkRef.current) return;
    const focus =
      initialFocus ??
      (typeof window !== 'undefined' ? parseHubParams(window.location.search) : { open: false });
    if (focus.open) {
      if (focus.status === 'error' && !toast) {
        setToast(
          {
            kind: 'error',
            message: `Integration setup failed${
              focus.errorCode ? `: ${focus.errorCode.replace(/_/g, ' ')}` : ''
            }`,
          },
        );
      }
      if (focus.mode) setSelectedMode(focus.mode);
      const target = catalog.find(
        (t) =>
          (!!focus.brand && (t.brandId === focus.brand || t.refId === focus.brand)) ||
          (!!focus.customMcpServerName && t.refId === focus.customMcpServerName),
      );
      if (target) setSelectedId(target.id);
      consumedDeeplinkRef.current = true;
      if (focus.status) {
        dispatchIntegrationChanged({
          reason: 'mcp_oauth_return',
          ref: focus.brand,
          agentConfigId,
        });
      }
      stripIntegrationDeeplink();
      return;
    }
    const dl =
      initialDeeplink ??
      (typeof window !== 'undefined'
        ? parseIntegrationDeeplink(window.location.search)
        : null);
    if (!dl) {
      consumedDeeplinkRef.current = true;
      return;
    }
    if (dl.toast?.kind === 'error' && !toast) setToast(dl.toast);
    if (dl.integrationQuery && !query) setQuery(dl.integrationQuery);
    const target = resolveDeeplinkTarget({
      targets: catalog,
      mcpConnect: dl.mcpConnect,
      openapiConnect: dl.openapiConnect,
      integrationType: dl.integrationType,
      integrationProvider: dl.integrationProvider,
    });
    if (target) setSelectedId(target.id);
    consumedDeeplinkRef.current = true;
      if (dl.toast) {
      // OAuth/return param signaled a state change in the agent's
      // skills.mcp / skills.openapi config; broadcast so the inline
      // section + monetization tab refresh without reload.
      dispatchIntegrationChanged({
        reason: 'mcp_oauth_return',
        ref: target?.refId,
        agentConfigId,
      });
    }
    stripIntegrationDeeplink();
  }, [open, loading, catalog, initialDeeplink, initialFocus, query, toast, agentConfigId]);

  // Refresh the hub's own snapshot when other components emit changes
  // (e.g. inline AgentIntegrations save, media-gen save).
  useIntegrationChanged(
    () => {
      if (!open) return;
      refresh();
    },
    { agentConfigId },
  );

  const selected = selectedId
    ? findTargetById(catalog, selectedId) ?? null
    : null;

  // Esc closes the dialog — same vocabulary as the workspace
  // Settings / Wallet / Analytics dialogs. Bound to `document`
  // (not the portal root) so it fires regardless of focus inside
  // the dialog body.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    // Mirrors the workspace's Settings / Wallet / Analytics dialog
    // (`components/workspace/fullscreen-nav.tsx`): centred glass
    // panel, 92vh tall, max 1200px wide, rounded-2xl + shadow,
    // tinted backdrop with click-to-dismiss. We portal the whole
    // thing to `document.body` so the modal escapes any positioned
    // / transformed ancestor — without the portal the dialog
    // collapses inside the agent-widget's transform stacking
    // context when the hub is opened from inside the workspace,
    // which is the squashed corner-of-the-viewport rendering the
    // user reported.
    <PopoverPortal
      role="dialog"
      ariaLabel={t('agentTools.manageIntegrationsFor', { agent: agentUsername })}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      <div
        className="absolute inset-0"
        aria-hidden
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      />
      <div
        className="glass-panel relative flex h-[92vh] w-[min(1200px,96vw)] flex-col overflow-hidden rounded-2xl shadow-2xl"
        onMouseDown={(e) => {
          // Keep drags started inside the panel from bubbling out
          // to the backdrop click-to-dismiss handler above.
          e.stopPropagation();
        }}
      >
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <h2 className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight text-foreground">
            {t('agentTools.manageIntegrations')}{' '}
            <span className="text-purple-500">@{agentUsername}</span>
          </h2>
          <button
            type="button"
            aria-label={t('chatSidebar.close')}
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {toast && (
          <div
            role="status"
            className={`px-3 sm:px-4 py-2 text-sm flex items-start justify-between gap-3 ${
              toast.kind === 'success'
                ? 'bg-green-50 text-green-900 border-b border-green-200'
                : 'bg-red-50 text-red-900 border-b border-red-200'
            }`}
          >
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="opacity-70 hover:opacity-100"
              aria-label={t('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[minmax(260px,1fr)_minmax(320px,1.4fr)]">
          <div className={`${selected ? 'hidden md:block' : 'block'} min-h-0 overflow-y-auto border-b border-border bg-muted/[0.03] md:border-b-0 md:border-r`}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" tone="muted" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                {t('agentTools.noIntegrationsAvailable')}
              </div>
            ) : (
              <ul role="listbox">
                {filtered.map((target) => {
                  const active = target.id === selectedId;
                  return (
                    <li key={target.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          setSelectedId(target.id);
                          setSelectedMode(target.availableModes[0] ?? null);
                        }}
                        className={`w-full text-left p-3 flex items-center gap-3 border-b border-border/50 transition-colors ${
                          active ? 'bg-muted/40' : 'hover:bg-muted/20'
                        }`}
                      >
                        <ProviderIcon slug={target.iconSlug} size={18} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {target.name}
                          </div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {target.description ?? ownershipDescription(target.ownership, target.name)}
                            {target.regionNote ? ` · ${target.regionNote}` : ''}
                          </div>
                        </div>
                        {!isAddTarget(target) && <ToggleIndicator value={isTargetConnected(target)} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className={`${selected ? 'flex' : 'hidden md:flex'} min-h-0 flex-col overflow-hidden bg-background`}>
            {selected ? (
              <div className="min-h-0 flex-1 overflow-hidden p-3 sm:p-4">
                <IntegrationDetail
                  target={selected}
                  agentConfigId={agentConfigId}
                  agentUserId={agentUserId}
                  agentUsername={agentUsername}
                  selectedMode={selectedMode ?? selected.availableModes[0] ?? null}
                  onModeChange={setSelectedMode}
                  featuredMcp={featuredMcps.find(
                    (item) =>
                      item.id === selected.modeRefs?.mcp_hosted ||
                      item.id === selected.refId,
                  )}
                  refreshVersion={refreshVersion}
                  onChanged={() => {
                    refresh();
                    onChanged?.();
                  }}
                  onDisabled={applyLocalDisable}
                  onDeleted={applyLocalDelete}
                  onClose={onClose}
                  onBack={() => setSelectedId(null)}
                />
              </div>
            ) : (
              <div className="p-3 text-sm text-muted-foreground sm:p-4">
                {t('agentTools.selectIntegration')}
              </div>
            )}
          </div>
        </div>
      </div>
    </PopoverPortal>
  );
}

interface DetailProps {
  target: IntegrationTarget;
  agentConfigId: string;
  agentUserId: string;
  agentUsername: string;
  selectedMode: HubMode | null;
  onModeChange: (mode: HubMode) => void;
  featuredMcp?: FeaturedMcpEntry;
  onChanged: () => void;
  onDisabled?: (target: IntegrationTarget) => void;
  onDeleted?: (target: IntegrationTarget) => void;
  onClose: () => void;
  onBack: () => void;
  connectNonce?: number;
  refreshVersion?: number;
  /**
   * Forget the integration's stored credentials. Forwarded by
   * IntegrationDetail so the BYO panels (Discord/Telegram) can render
   * an inline "Forget stored credentials" link in their footer.
   */
  onForget?: () => Promise<void>;
}

function IntegrationDetail({
  target,
  agentConfigId,
  agentUserId,
  agentUsername,
  selectedMode,
  onModeChange,
  featuredMcp,
  onChanged,
  onDisabled,
  onDeleted,
  onClose,
  onBack,
  refreshVersion = 0,
}: DetailProps) {
  const t = useT();
  const mode = selectedMode ?? target.availableModes[0] ?? null;
  const [connectNonce, setConnectNonce] = useState(0);
  const enableIntegrationRow = useCallback(async () => {
    if (!target.integrationRowId) return false;
    const response = await fetch(
      `/api/agents/${agentConfigId}/integrations/${target.integrationRowId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isEnabled: true }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error ?? `enable_failed_${response.status}`);
    dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: target.refId, agentConfigId });
    onChanged();
    return true;
  }, [agentConfigId, onChanged, target]);

  const startConnect = useCallback(() => {
    if (
      target.integrationRowId &&
      target.status === 'available' &&
      (target.ownership !== 'agent_byo_app' || !!target.connectedAccount)
    ) {
      void enableIntegrationRow();
      return;
    }
    if (target.kind === 'custom_mcp' && target.mcpConfig) {
      void enableSavedMcp({
        agentUserId,
        agentConfigId,
        serverName: target.modeRefs?.mcp_hosted ?? target.refId,
        config: target.mcpConfig,
        catalog: featuredMcp,
        onChanged,
      });
      return;
    }
    if (target.kind === 'openapi' && target.openapiConfig) {
      void enableSavedOpenApi({
        agentUserId,
        agentConfigId,
        serverName: target.modeRefs?.openapi ?? target.refId,
        config: target.openapiConfig,
        onChanged,
      });
      return;
    }
    if (mode === 'mcp_hosted' || target.kind === 'featured_mcp') {
      setConnectNonce((value) => value + 1);
      return;
    }
    if (mode === 'api_key' || target.kind === 'media_gen') {
      // API-key integrations need form input before saving. The detail panel
      // below renders the provider-specific form with its own Save button.
      return;
    }
    if (target.brandId === 'telegram') {
      setConnectNonce((value) => value + 1);
      return;
    }
    if (target.brandId === 'discord' || target.ownership === 'agent_byo_app') {
      setConnectNonce((value) => value + 1);
      return;
    }
    if (mode === 'messaging') {
      window.location.href = `/api/auth/connect/${target.modeRefs?.messaging ?? target.refId}?returnTo=${encodeURIComponent(
        `/u/${agentUsername}?tab=settings&hub=1&hubBrand=${target.brandId}&hubMode=messaging`,
      )}`;
      return;
    }
    window.location.href = `/api/auth/connect/${target.modeRefs?.oauth ?? target.refId}?returnTo=${encodeURIComponent(
      `/u/${agentUsername}?tab=settings&hub=1&hubBrand=${target.brandId}&hubMode=oauth`,
    )}`;
  }, [agentConfigId, agentUserId, agentUsername, enableIntegrationRow, featuredMcp, mode, onChanged, target]);

  const forgetIntegration = useCallback(async () => {
    onDeleted?.(target);
    if (target.kind === 'featured_mcp' || target.kind === 'custom_mcp') {
      const serverName = target.modeRefs?.mcp_hosted ?? target.refId;
      const response = await fetch(
        `/api/agents/${agentUserId}/mcp?name=${encodeURIComponent(serverName)}`,
        { method: 'DELETE' },
      );
      if (!response.ok) throw new Error(`forget_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'mcp_save', ref: serverName, agentConfigId });
      onChanged();
      return;
    }
    if (target.kind === 'openapi') {
      const serverName = target.modeRefs?.openapi ?? target.refId;
      const response = await fetch(
        `/api/agents/${agentUserId}/openapi?name=${encodeURIComponent(serverName)}`,
        { method: 'DELETE' },
      );
      if (!response.ok) throw new Error(`forget_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'openapi_save', ref: serverName, agentConfigId });
      onChanged();
      return;
    }
    if (target.integrationRowId) {
      const response = await fetch(
        `/api/agents/${agentConfigId}/integrations/${target.integrationRowId}`,
        { method: 'DELETE' },
      );
      if (!response.ok) throw new Error(`forget_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: target.refId, agentConfigId });
      onChanged();
    }
  }, [agentConfigId, agentUserId, onChanged, onDeleted, target]);

  return (
    <div className="flex h-full min-h-0 flex-col space-y-3">
      <button
        type="button"
        onClick={onBack}
        className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:hidden"
      >
        <ChevronLeft className="h-4 w-4" />
        Integrations
      </button>
      <div className="flex shrink-0 items-start gap-3">
        <ProviderIcon slug={target.iconSlug} size={28} />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold">{target.name}</h3>
          <p className="text-xs text-muted-foreground">
            {target.description ?? ownershipDescription(target.ownership, target.name)}
          </p>
        </div>
        {!isAddTarget(target) && (
          <IntegrationToggle
            target={target}
            agentConfigId={agentConfigId}
            agentUserId={agentUserId}
            onChanged={onChanged}
            onDisabled={onDisabled}
            onConnect={startConnect}
          />
        )}
      </div>

      {target.availableModes.length > 1 && (
        <div className="rounded-md border border-border p-2 space-y-1">
          <div className="text-xs font-medium text-muted-foreground">{t('agentTools.setupMode')}</div>
          <div className="flex flex-wrap gap-1.5">
            {target.availableModes.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onModeChange(m)}
                className={`rounded-md border px-2 py-1 text-xs ${
                  mode === m
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted/30'
                }`}
              >
                {modeLabel(m)}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isTargetConnected(target) &&
        target.kind !== 'custom_mcp' &&
        target.kind !== 'openapi' &&
        target.kind !== 'custom_mcp_add' &&
        target.kind !== 'custom_openapi_add' &&
        // Discord/Telegram BYO panels render their own form CTA + inline
        // Forget link, so the generic disabled-actions panel would create
        // a redundant second "Connect" button.
        target.brandId !== 'discord' &&
        target.brandId !== 'telegram' && (
          <DisabledIntegrationActions
            label={target.name}
            onConnect={async () => startConnect()}
            onForget={forgetIntegration}
            canForget={!!target.integrationRowId || !!target.mcpConfig}
          />
        )}

      <NextStep
        target={target}
        agentConfigId={agentConfigId}
        agentUserId={agentUserId}
        agentUsername={agentUsername}
        selectedMode={mode}
        onModeChange={onModeChange}
        featuredMcp={featuredMcp}
        onChanged={onChanged}
        onDisabled={onDisabled}
        onDeleted={onDeleted}
        onClose={onClose}
        onBack={onBack}
        connectNonce={connectNonce}
        refreshVersion={refreshVersion}
        onForget={forgetIntegration}
      />

      {/*
        Per-integration BYO OAuth-client controls. Only render for the
        integration kinds that resolve through `resolveOAuthClient`
        (MCP / messaging / posting). The panel auto-hides for kinds it
        doesn't recognise (custom_mcp without an integrationId, OpenAPI,
        identity-only providers, byo_app providers like Discord/Telegram).
      */}
      {target.refId &&
        mode !== 'mcp_hosted' &&
        mode !== 'mcp_stdio' &&
        (target.kind === 'messaging' || target.kind === 'platform') && (
          <div className="pt-1">
            <IntegrationOAuthClientPanel
              agentConfigId={agentConfigId}
              integrationId={target.refId}
              mode={mode ?? 'oauth'}
              onChanged={onChanged}
            />
          </div>
        )}
    </div>
  );
}

function IntegrationToggle({
  target,
  agentConfigId,
  agentUserId,
  onChanged,
  onDisabled,
  onConnect,
}: {
  target: IntegrationTarget;
  agentConfigId: string;
  agentUserId: string;
  onChanged: () => void;
  onDisabled?: (target: IntegrationTarget) => void;
  onConnect: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const actualConnected = isTargetConnected(target);
  const [optimisticConnected, setOptimisticConnected] = useState<boolean | null>(null);
  const connected = optimisticConnected ?? actualConnected;

  useEffect(() => {
    setOptimisticConnected((current) => (current === actualConnected ? null : current));
  }, [actualConnected, target.id]);

  useEffect(() => {
    const onFailed = (event: Event) => {
      const detail = (event as CustomEvent<{ kind?: string; ref?: string }>).detail;
      if (!detail || detail.ref !== target.refId) return;
      setOptimisticConnected(false);
    };
    window.addEventListener('integration-connect-failed', onFailed);
    return () => window.removeEventListener('integration-connect-failed', onFailed);
  }, [target.refId]);

  async function disable() {
    setOptimisticConnected(false);
    onDisabled?.(target);
    setBusy(true);
    try {
      if (target.kind === 'featured_mcp' || target.kind === 'custom_mcp') {
        const serverName = target.modeRefs?.mcp_hosted ?? target.refId;
        const cfg = target.mcpConfig;
        if (!cfg) throw new Error('missing_mcp_config');
        const response = await fetch(`/api/agents/${agentUserId}/mcp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: serverName,
            enabled: false,
            catalogId: target.kind === 'featured_mcp' ? (cfg.catalogId ?? serverName) : undefined,
            url: cfg.url,
            authType: cfg.authType ?? 'oauth2',
            authHeaderName: cfg.authHeaderName,
            transport: cfg.transport,
            mcpUrlTemplate: cfg.mcpUrlTemplate,
            toolPolicies: cfg.toolPolicies,
          }),
        });
        if (!response.ok) throw new Error(`disconnect_failed_${response.status}`);
        dispatchIntegrationChanged({ reason: 'mcp_save', ref: serverName, agentConfigId });
      } else if (target.kind === 'openapi') {
        const serverName = target.modeRefs?.openapi ?? target.refId;
        const cfg = target.openapiConfig;
        if (!cfg) throw new Error('missing_openapi_config');
        const response = await fetch(`/api/agents/${agentUserId}/openapi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: serverName,
            enabled: false,
            specUrl: cfg.specUrl,
            baseUrl: cfg.baseUrl,
            authType: cfg.authType ?? 'none',
            authHeaderName: cfg.authHeaderName,
            operationPolicies: cfg.operationPolicies,
          }),
        });
        if (!response.ok) throw new Error(`disconnect_failed_${response.status}`);
        dispatchIntegrationChanged({ reason: 'openapi_save', ref: serverName, agentConfigId });
      } else if (target.integrationRowId) {
        const response = await fetch(
          `/api/agents/${agentConfigId}/integrations/${target.integrationRowId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isEnabled: false }),
          },
        );
        if (!response.ok) throw new Error(`disable_failed_${response.status}`);
        dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: target.refId, agentConfigId });
      }
      onChanged();
    } catch (err) {
      setOptimisticConnected(true);
      onChanged();
      console.error('integration_toggle_disable_failed', err);
    } finally {
      setBusy(false);
    }
  }

  function enable() {
    setOptimisticConnected(true);
    onConnect();
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={connected}
      aria-label={connected ? `Disable ${target.name}` : `Connect ${target.name}`}
      onClick={connected ? disable : enable}
      disabled={busy}
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        connected ? 'bg-emerald-600/90 ring-1 ring-emerald-400/40' : 'bg-muted-foreground/30 ring-1 ring-border'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          connected ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function ownershipDescription(
  ownership: IntegrationTarget['ownership'],
  name: string,
): string {
  switch (ownership) {
    case 'agent_oauth':
      return `Connect ${name} to this agent.`;
    case 'agent_byo_app':
      return `Add ${name} app credentials.`;
    case 'agent_api_key':
      return `Add a ${name} API key.`;
    case 'agent_mcp':
      return `MCP server for this agent.`;
    case 'agent_openapi':
      return `OpenAPI server for this agent.`;
    case 'personal_identity':
      return `${name} is a personal login account.`;
  }
}

async function enableSavedMcp(args: {
  agentUserId: string;
  agentConfigId: string;
  serverName: string;
  config: McpSkillEntry;
  catalog?: FeaturedMcpEntry;
  onChanged: () => void;
}) {
  const response = await fetch(`/api/agents/${args.agentUserId}/mcp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: args.serverName,
      enabled: true,
      catalogId: args.catalog ? (args.config.catalogId ?? args.catalog.id) : undefined,
      url: args.config.url ?? args.catalog?.mcpUrl,
      authType: args.config.authType ?? args.catalog?.authType ?? 'oauth2',
      authHeaderName: args.config.authHeaderName,
      transport: args.config.transport ?? args.catalog?.transport,
      mcpUrlTemplate: args.config.mcpUrlTemplate ?? args.catalog?.mcpUrlTemplate,
      toolPolicies: args.config.toolPolicies,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error ?? `enable_failed_${response.status}`);
  dispatchIntegrationChanged({ reason: 'mcp_save', ref: args.serverName, agentConfigId: args.agentConfigId });
  args.onChanged();
}

async function enableSavedOpenApi(args: {
  agentUserId: string;
  agentConfigId: string;
  serverName: string;
  config: OpenApiSkillEntry;
  onChanged: () => void;
}) {
  const response = await fetch(`/api/agents/${args.agentUserId}/openapi`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: args.serverName,
      enabled: true,
      specUrl: args.config.specUrl,
      baseUrl: args.config.baseUrl,
      authType: args.config.authType ?? 'none',
      authHeaderName: args.config.authHeaderName,
      operationPolicies: args.config.operationPolicies,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error ?? `enable_failed_${response.status}`);
  dispatchIntegrationChanged({ reason: 'openapi_save', ref: args.serverName, agentConfigId: args.agentConfigId });
  args.onChanged();
}

function NextStep({
  target,
  agentConfigId,
  agentUserId,
  agentUsername,
  selectedMode,
  featuredMcp,
  onChanged,
  onDisabled,
  onDeleted,
  connectNonce = 0,
  refreshVersion = 0,
  onForget,
}: DetailProps) {
  const t = useT();
  const mode = selectedMode ?? target.availableModes[0] ?? null;

  if (target.kind === 'custom_mcp_add') {
    return (
      <CustomMcpAddPanel
        agentUserId={agentUserId}
        agentConfigId={agentConfigId}
        onChanged={onChanged}
        connectNonce={connectNonce}
      />
    );
  }

  if (target.kind === 'custom_openapi_add') {
    return (
      <CustomOpenApiAddPanel
        agentUserId={agentUserId}
        agentConfigId={agentConfigId}
        onChanged={onChanged}
        connectNonce={connectNonce}
      />
    );
  }

  if (target.status === 'connected' && target.tools && target.integrationRowId) {
    return (
      <ConnectedPlatformToolsPanel
        target={target}
        agentConfigId={agentConfigId}
        onChanged={onChanged}
      />
    );
  }

  if (mode === 'mcp_hosted' || target.kind === 'featured_mcp' || target.kind === 'custom_mcp') {
    if (target.kind === 'custom_mcp' && target.mcpConfig?.enabled === false) {
      const serverName = target.modeRefs?.mcp_hosted ?? target.refId;
      return (
        <DisabledIntegrationActions
          label={t('agentTools.mcpServer')}
          onConnect={() =>
            enableSavedMcp({
              agentUserId,
              agentConfigId,
              serverName,
              config: target.mcpConfig!,
              catalog: featuredMcp,
              onChanged,
            })
          }
          onForget={async () => {
            onDeleted?.(target);
            const response = await fetch(
              `/api/agents/${agentUserId}/mcp?name=${encodeURIComponent(serverName)}`,
              { method: 'DELETE' },
            );
            if (!response.ok) throw new Error(`delete_failed_${response.status}`);
            dispatchIntegrationChanged({ reason: 'mcp_save', ref: serverName, agentConfigId });
            onChanged();
          }}
        />
      );
    }
    if (target.mcpConfig || target.kind === 'custom_mcp') {
      return (
        <ConnectedMcpPanel
          serverName={target.modeRefs?.mcp_hosted ?? target.refId}
          agentUserId={agentUserId}
          agentConfigId={agentConfigId}
          mcpConfig={target.mcpConfig}
          catalog={featuredMcp}
          initialPolicies={target.toolPolicies}
          onChanged={onChanged}
          refreshVersion={refreshVersion}
          onDelete={async () => {
            onDeleted?.(target);
            const serverName = target.modeRefs?.mcp_hosted ?? target.refId;
            const response = await fetch(
              `/api/agents/${agentUserId}/mcp?name=${encodeURIComponent(serverName)}`,
              { method: 'DELETE' },
            );
            if (!response.ok) throw new Error(`delete_failed_${response.status}`);
            dispatchIntegrationChanged({ reason: 'mcp_save', ref: serverName, agentConfigId });
            onChanged();
          }}
        />
      );
    }
    if (!featuredMcp) {
      return (
        <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
          {t('agentTools.mcpAlreadyConnected')}
        </div>
      );
    }
    return (
      <FeaturedMcpConnectPanel
        catalog={featuredMcp}
        agentUserId={agentUserId}
        agentConfigId={agentConfigId}
        onChanged={onChanged}
        connectNonce={connectNonce}
      />
    );
  }

  if (mode === 'openapi' || target.kind === 'openapi') {
    if (target.kind === 'openapi' && target.openapiConfig?.enabled === false) {
      const serverName = target.modeRefs?.openapi ?? target.refId;
      return (
        <DisabledIntegrationActions
          label={t('agentTools.openapiServer')}
          onConnect={() =>
            enableSavedOpenApi({
              agentUserId,
              agentConfigId,
              serverName,
              config: target.openapiConfig!,
              onChanged,
            })
          }
          onForget={async () => {
            onDeleted?.(target);
            const response = await fetch(
              `/api/agents/${agentUserId}/openapi?name=${encodeURIComponent(serverName)}`,
              { method: 'DELETE' },
            );
            if (!response.ok) throw new Error(`delete_failed_${response.status}`);
            dispatchIntegrationChanged({ reason: 'openapi_save', ref: serverName, agentConfigId });
            onChanged();
          }}
        />
      );
    }
    return (
      <ConnectedOpenApiPanel
        serverName={target.modeRefs?.openapi ?? target.refId}
        agentUserId={agentUserId}
        agentConfigId={agentConfigId}
        openapiConfig={target.openapiConfig}
        initialPolicies={target.openapiConfig?.operationPolicies}
        onChanged={onChanged}
        refreshVersion={refreshVersion}
        onDelete={async () => {
          onDeleted?.(target);
          const serverName = target.modeRefs?.openapi ?? target.refId;
          const response = await fetch(
            `/api/agents/${agentUserId}/openapi?name=${encodeURIComponent(serverName)}`,
            { method: 'DELETE' },
          );
          if (!response.ok) throw new Error(`delete_failed_${response.status}`);
          dispatchIntegrationChanged({ reason: 'openapi_save', ref: serverName, agentConfigId });
          onChanged();
        }}
      />
    );
  }

  if (mode === 'api_key' || target.kind === 'media_gen') {
    return (
      <ApiKeyConnectPanel
        providerId={target.modeRefs?.api_key ?? target.refId}
        providerName={target.name}
        capabilityIds={Object.keys(target.capabilityToolMap ?? {})}
        agentUserId={agentUserId}
        agentConfigId={agentConfigId}
        onChanged={onChanged}
        connectNonce={connectNonce}
      />
    );
  }

  if (target.brandId === 'telegram' || mode === 'messaging') {
    if (target.brandId === 'telegram') {
      return (
        <TelegramBotConnectPanel
          agentUserId={agentUserId}
          agentConfigId={agentConfigId}
          onChanged={onChanged}
          connectNonce={connectNonce}
          onForget={onForget}
          hasStoredCredentials={!!target.connectedAccount}
        />
      );
    }
    if (target.brandId === 'discord') {
      return (
        <DiscordAppConnectPanel
          agentUserId={agentUserId}
          agentConfigId={agentConfigId}
          onChanged={onChanged}
          connectNonce={connectNonce}
          onForget={onForget}
          hasStoredCredentials={!!target.connectedAccount}
        />
      );
    }
    return null;
  }

  return null;
}

function DisabledIntegrationActions({
  label,
  onConnect,
  onForget,
  canForget = true,
}: {
  label: string;
  onConnect: () => Promise<void>;
  onForget: () => Promise<void>;
  canForget?: boolean;
}) {
  const t = useT();
  const [busy, setBusy] = useState<'connect' | 'forget' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(kind: 'connect' | 'forget', action: () => Promise<void>) {
    setBusy(kind);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to ${kind} ${label}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <p className="text-xs text-muted-foreground">
        This {label} is disabled. Connect to re-enable it with stored credentials, or forget it to remove stored setup.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1 text-sm"
          disabled={busy !== null}
          onClick={() => run('connect', onConnect)}
        >
          {busy === 'connect' ? t('voice.connecting') : t('actions.connect')}
        </button>
        {canForget && (
          <button
            type="button"
            className="btn-ghost flex-1 border border-red-500/30 text-sm text-red-500 hover:bg-red-500/10"
            disabled={busy !== null}
            onClick={() => run('forget', onForget)}
          >
            {busy === 'forget' ? t('agentTools.forgetting') : t('agentTools.forget')}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function ConnectedMcpPanel({
  serverName,
  agentUserId,
  agentConfigId,
  mcpConfig,
  catalog,
  initialPolicies,
  onChanged,
  refreshVersion = 0,
  isCustom = false,
  onDelete,
}: {
  serverName: string;
  agentUserId: string;
  agentConfigId: string;
  mcpConfig?: McpSkillEntry;
  catalog?: FeaturedMcpEntry;
  initialPolicies?: Record<string, ToolPolicyValue>;
  onChanged: () => void;
  refreshVersion?: number;
  isCustom?: boolean;
  onDelete?: () => Promise<void>;
}) {
  const t = useT();
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tools, setTools] = useState<Array<{ name: string; description: string }> | null>(null);
  const [toolsError, setToolsError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Record<string, ToolPolicyValue>>(initialPolicies ?? {});

  useEffect(() => {
    let cancelled = false;
    setTools(null);
    setToolsError(null);
    fetch(`/api/agents/${agentUserId}/mcp?name=${encodeURIComponent(serverName)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error ?? `tools_failed_${response.status}`);
        if (!cancelled) setTools((data.tools as Array<{ name: string; description: string }>) ?? []);
      })
      .catch((err) => {
        if (!cancelled) setToolsError(err instanceof Error ? err.message : 'Unable to load tools');
      });
    return () => {
      cancelled = true;
    };
  }, [agentUserId, refreshVersion, serverName]);

  useEffect(() => {
    setPolicies(initialPolicies ?? {});
  }, [initialPolicies, serverName]);

  async function savePolicies(next: Record<string, ToolPolicyValue>) {
    setPolicies(next);
    setSavingPolicies(true);
    setError(null);
    try {
      const response = await fetch(`/api/agents/${agentUserId}/mcp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: serverName,
          catalogId: mcpConfig?.catalogId ?? serverName,
          url: mcpConfig?.url ?? catalog?.mcpUrl,
          authType: mcpConfig?.authType ?? catalog?.authType ?? 'oauth2',
          transport: mcpConfig?.transport ?? catalog?.transport,
          mcpUrlTemplate: mcpConfig?.mcpUrlTemplate ?? catalog?.mcpUrlTemplate,
          toolPolicies: next,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error ?? `save_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'mcp_save', ref: serverName, agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save tool policies');
    } finally {
      setSavingPolicies(false);
    }
  }

  async function deleteCustom() {
    if (!onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete MCP server');
      onChanged();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-3 rounded-md border border-border p-3">
      {isCustom && (
        <div className="flex shrink-0 gap-2">
          <button type="button" className="btn-secondary flex-1 text-sm" onClick={onChanged}>
            Connect
          </button>
          <button
            type="button"
            className="btn-ghost flex-1 border border-red-500/30 text-sm text-red-500 hover:bg-red-500/10"
            disabled={deleting}
            onClick={deleteCustom}
          >
            {deleting ? t('agentTools.deleting') : t('messages.delete')}
          </button>
        </div>
      )}
      <p className="shrink-0 text-xs text-muted-foreground">
        {tools
          ? `${tools.length} ${tools.length === 1 ? 'tool' : 'tools'} available`
          : toolsError
            ? t('agentTools.toolsUnavailable')
            : t('agentTools.loadingTools')}
      </p>

      {tools && (
        <ToolPolicyEditor
          available={tools as ToolPolicyItem[]}
          policies={policies}
          onChange={savePolicies}
          label={t('agents.tools')}
          defaultScope="everyone"
          fill
        />
      )}

      {savingPolicies && <p className="text-xs text-muted-foreground">Saving...</p>}
      {toolsError && <p className="text-xs text-muted-foreground">{toolsError}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function ConnectedOpenApiPanel({
  serverName,
  agentUserId,
  agentConfigId,
  openapiConfig,
  initialPolicies,
  onChanged,
  refreshVersion = 0,
  isCustom = false,
  onDelete,
}: {
  serverName: string;
  agentUserId: string;
  agentConfigId: string;
  openapiConfig?: OpenApiSkillEntry;
  initialPolicies?: Record<string, ToolPolicyValue>;
  onChanged: () => void;
  refreshVersion?: number;
  isCustom?: boolean;
  onDelete?: () => Promise<void>;
}) {
  const t = useT();
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operations, setOperations] = useState<Array<{ name: string; description: string }> | null>(null);
  const [operationsError, setOperationsError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Record<string, ToolPolicyValue>>(initialPolicies ?? {});

  useEffect(() => {
    let cancelled = false;
    setOperations(null);
    setOperationsError(null);
    fetch(`/api/agents/${agentUserId}/openapi?name=${encodeURIComponent(serverName)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error ?? `operations_failed_${response.status}`);
        const items = (data.operations as Array<{ name: string; description?: string }>) ?? [];
        if (!cancelled) {
          setOperations(items.map((item) => ({ name: item.name, description: item.description ?? '' })));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setOperationsError(err instanceof Error ? err.message : 'Unable to load OpenAPI operations');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentUserId, refreshVersion, serverName]);

  useEffect(() => {
    setPolicies(initialPolicies ?? {});
  }, [initialPolicies, serverName]);

  async function savePolicies(next: Record<string, ToolPolicyValue>) {
    if (!openapiConfig?.specUrl) return;
    setPolicies(next);
    setSavingPolicies(true);
    setError(null);
    try {
      const response = await fetch(`/api/agents/${agentUserId}/openapi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: serverName,
          specUrl: openapiConfig.specUrl,
          baseUrl: openapiConfig.baseUrl,
          authType: openapiConfig.authType ?? 'none',
          authHeaderName: openapiConfig.authHeaderName,
          operationPolicies: next,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error ?? `save_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'openapi_save', ref: serverName, agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save operation policies');
    } finally {
      setSavingPolicies(false);
    }
  }

  async function deleteCustom() {
    if (!onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete OpenAPI server');
      onChanged();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-3 rounded-md border border-border p-3">
      {isCustom && (
        <div className="flex shrink-0 gap-2">
          <button type="button" className="btn-secondary flex-1 text-sm" onClick={onChanged}>
            Connect
          </button>
          <button
            type="button"
            className="btn-ghost flex-1 border border-red-500/30 text-sm text-red-500 hover:bg-red-500/10"
            disabled={deleting}
            onClick={deleteCustom}
          >
            {deleting ? t('agentTools.deleting') : t('messages.delete')}
          </button>
        </div>
      )}
      <p className="shrink-0 text-xs text-muted-foreground">
        {operations
          ? `${operations.length} ${operations.length === 1 ? 'operation' : 'operations'} available`
          : operationsError
            ? t('agentTools.operationsUnavailable')
            : t('agentTools.loadingOperations')}
      </p>

      {operations && (
        <ToolPolicyEditor
          available={operations as ToolPolicyItem[]}
          policies={policies}
          onChange={savePolicies}
          label={t('agentTools.operations')}
          defaultScope="owner"
          fill
        />
      )}

      {savingPolicies && <p className="text-xs text-muted-foreground">Saving...</p>}
      {operationsError && <p className="text-xs text-muted-foreground">{operationsError}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function ConnectedPlatformToolsPanel({
  target,
  agentConfigId,
  onChanged,
}: {
  target: IntegrationTarget;
  agentConfigId: string;
  onChanged: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [policies, setPolicies] = useState<Record<string, ToolPolicyValue>>(
    target.toolPolicies ?? {},
  );
  const [error, setError] = useState<string | null>(null);
  const tools = target.tools ?? [];
  const accountSummary = accountSummaryFor(target);
  const inboundCopy = inboundCopyFor(target);

  useEffect(() => {
    setPolicies(target.toolPolicies ?? {});
  }, [target.id, target.toolPolicies]);

  async function persist(nextPolicies: Record<string, ToolPolicyConfig>, enabled = true) {
    if (!target.integrationRowId) return;
    setBusy(true);
    setError(null);
    setPolicies(nextPolicies);
    try {
      const enabledTools = new Set(
        Object.entries(nextPolicies)
          .filter(([, policy]) => policy.policy !== 'block')
          .map(([tool]) => tool),
      );
      const enabledCapabilities = Object.entries(target.capabilityToolMap ?? {})
        .filter(([, capTools]) => capTools.some((tool) => enabledTools.has(tool)))
        .map(([capability]) => capability);
      const response = await fetch(
        `/api/agents/${agentConfigId}/integrations/${target.integrationRowId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isEnabled: enabled,
            enabledCapabilities,
            metadata: {
              ...(target.metadata ?? {}),
              toolPolicies: nextPolicies,
            },
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error ?? `save_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: target.refId, agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save tool policies');
    } finally {
      setBusy(false);
    }
  }

  // Discord bots are useless until the owner installs them into a guild.
  // We persist the applicationId on connect (see /api/auth/connect/discord),
  // which is the only datum needed to synthesize the OAuth2 invite URL —
  // permissions are a stable bitfield. Webhook-mode rows have no
  // applicationId, so the link only shows for the bot path.
  const discordInstallUrl = useMemo(() => {
    if (target.refId !== 'discord') return null;
    const meta = target.connectedAccount?.metadata as
      | Record<string, unknown>
      | null
      | undefined;
    const applicationId = typeof meta?.applicationId === 'string' ? meta.applicationId : null;
    if (!applicationId) return null;
    return buildDiscordInstallUrl({ applicationId });
  }, [target.refId, target.connectedAccount]);

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-3 rounded-md border border-border p-3">
      {(accountSummary || inboundCopy || discordInstallUrl) && (
        <div className="shrink-0 rounded-md border border-border bg-muted/10 p-2.5 text-xs">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {accountSummary && <div className="font-medium">{accountSummary}</div>}
              {inboundCopy && (
                <div className={accountSummary ? 'mt-1 text-muted-foreground' : 'text-muted-foreground'}>
                  {inboundCopy}
                </div>
              )}
            </div>
            {discordInstallUrl && (
              <a
                href={discordInstallUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-primary shrink-0 self-start whitespace-nowrap text-[11px] px-2.5 py-1"
                title={t('agentTools.addToDiscordHint')}
              >
                {t('agentTools.addToDiscord')} ↗
              </a>
            )}
          </div>
        </div>
      )}
      <p className="shrink-0 text-xs text-muted-foreground">
        {tools.length} {tools.length === 1 ? 'tool' : 'tools'} available
      </p>
      <ToolPolicyEditor
        available={tools}
        policies={policies}
        // Messaging tools read a row with no saved scope as its suggested
        // scope at runtime (normalizeToolPolicies in the factory), so writing
        // that suggestion here changes nothing it does.
        onChange={(next) => persist(normalizeToolPolicies(next), true)}
        label={t('agents.tools')}
        fill
      />
      {busy && <p className="text-xs text-muted-foreground">Saving...</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function accountSummaryFor(target: IntegrationTarget): string | null {
  const account = target.connectedAccount;
  if (!account) {
    if (target.ownership === 'agent_api_key') return `${target.name} API key connected`;
    return null;
  }

  const meta = account.metadata ?? {};
  const displayName =
    typeof meta.displayName === 'string'
      ? meta.displayName
      : typeof meta.botName === 'string'
        ? meta.botName
        : account.providerUsername
          ? `@${account.providerUsername}`
          : account.providerUserId;
  const suffix = account.providerUsername && !String(displayName).startsWith('@')
    ? ` (@${account.providerUsername})`
    : '';

  if (target.refId === 'telegram') {
    return `Bot connected: ${displayName ?? 'Telegram bot'}${suffix}`;
  }
  if (target.refId === 'discord') {
    return `App connected: ${displayName ?? account.providerUserId ?? 'Discord app'}`;
  }
  if (account.authType === 'api_key') {
    return `${target.name} API key connected`;
  }
  return `Account connected: ${displayName ?? target.name}`;
}

function inboundCopyFor(target: IntegrationTarget): string | null {
  if (!target.categories.includes('messaging')) return null;
  switch (target.refId) {
    case 'telegram':
      return 'Incoming Telegram messages to this bot are delivered to this agent; replies use the enabled send tools.';
    case 'discord':
      return 'Incoming Discord interactions for this app are delivered to this agent; replies use the enabled Discord tools.';
    case 'slack':
      return 'Incoming Slack events for this app are delivered to this agent when webhook handling is configured.';
    default:
      return `Incoming ${target.name} traffic is routed to this agent when the provider webhook is configured.`;
  }
}

function modeLabel(mode: HubMode): string {
  switch (mode) {
    case 'oauth':
      return 'OAuth';
    case 'messaging':
      return 'Messaging';
    case 'api_key':
      return 'API key';
    case 'mcp_hosted':
      return 'Hosted MCP';
    case 'mcp_stdio':
      return 'MCP';
    case 'openapi':
      return 'OpenAPI';
  }
}

function ApiKeyConnectPanel({
  providerId,
  providerName,
  capabilityIds,
  agentUserId,
  agentConfigId,
  onChanged,
  connectNonce = 0,
}: {
  providerId: string;
  providerName: string;
  capabilityIds: string[];
  agentUserId: string;
  agentConfigId: string;
  onChanged: () => void;
  connectNonce?: number;
}) {
  const t = useT();
  const [apiKey, setApiKey] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [inboundDomain, setInboundDomain] = useState('');
  const [inboundParseUrl, setInboundParseUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastConnectNonce = useRef(connectNonce);
  const isSendGrid = providerId === 'sendgrid';
  const canSubmit = !!apiKey.trim() && (!isSendGrid || !!fromEmail.trim());

  async function submit() {
    if (!canSubmit) {
      setError(isSendGrid ? t('agentTools.senderEmailRequired') : t('agentTools.apiKeyRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/auth/connect/${providerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: apiKey.trim(),
          agentId: agentUserId,
          ...(isSendGrid
            ? {
                fromEmail: fromEmail.trim(),
                inboundDomain: inboundDomain.trim() || undefined,
              }
            : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? `save_${res.status}`);
      setInboundParseUrl(typeof data.inboundParseUrl === 'string' ? data.inboundParseUrl : null);
      if (data.connectedAccountId) {
        const integrationRes = await fetch(`/api/agents/${agentConfigId}/integrations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'platform',
            provider: providerId,
            enabledCapabilities: capabilityIds,
            isEnabled: true,
            connectedAccountId: data.connectedAccountId,
          }),
        });
        const integrationData = await integrationRes.json().catch(() => ({}));
        if (!integrationRes.ok) {
          throw new Error(integrationData.error ?? `integration_save_${integrationRes.status}`);
        }
      }
      dispatchIntegrationChanged({ reason: 'connected_account_change', ref: providerId, agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('agentTools.unableToSaveApiKey'));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (connectNonce === lastConnectNonce.current) return;
    lastConnectNonce.current = connectNonce;
    if (canSubmit) {
      submit();
    } else if (isSendGrid) {
      setError(t('agentTools.sendgridEnterBoth'));
    }
    // submit intentionally reads the current API key field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectNonce]);

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <div>
        <label className="text-xs font-medium">{providerName} API key</label>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            setError(null);
          }}
          className="input mt-1 w-full text-sm"
        />
      </div>
      {isSendGrid && (
        <>
          <div>
            <label className="text-xs font-medium">{t('agentTools.verifiedSenderEmail')}</label>
            <input
              type="email"
              value={fromEmail}
              onChange={(e) => {
                setFromEmail(e.target.value);
                setError(null);
              }}
              className="input mt-1 w-full text-sm"
              placeholder={t('agentTools.senderEmailPlaceholder')}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t('agentTools.verifiedSenderHint')}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium">{t('agentTools.inboundParseDomain')}</label>
            <input
              type="text"
              value={inboundDomain}
              onChange={(e) => {
                setInboundDomain(e.target.value);
                setError(null);
              }}
              className="input mt-1 w-full text-sm"
              placeholder="mail.example.com"
            />
            {inboundParseUrl && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {t('agentTools.configureInboundParse')} <code>{inboundParseUrl}</code>
              </p>
            )}
          </div>
        </>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
      {saving && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner size="sm" tone="muted" />
          <span>Saving...</span>
        </div>
      )}
      <button
        type="button"
        className="btn-primary w-full text-sm"
        disabled={saving || !canSubmit}
        onClick={submit}
      >
        {saving ? <Spinner size="sm" /> : `Save ${providerName}`}
      </button>
    </div>
  );
}

/**
 * Discord BYO connect panel.
 *
 * Two tabs:
 *   - **Webhook** — paste a channel webhook URL; one-channel post-only.
 *   - **Bot** — paste a Bot Token; the server derives applicationId +
 *     publicKey via fetchDiscordAppIdentity and pushes the agent's
 *     identity (username + avatar) onto the Discord App.
 *
 * Identity sync: handled fully server-side. Discord rate-limits bot
 * username changes to ~2/hour, so the response may include a
 * `warnings` array (e.g. ["username_rate_limited"]) which we surface
 * as a non-blocking notice. Pre-connect the panel shows generic
 * Developer Portal links; once connected we replace them with
 * per-app deeplinks (we know the applicationId).
 *
 * Why no Client ID / Client Secret fields: bot tokens are not OAuth
 * tokens and Discord exposes no programmatic Application creation /
 * client_credentials → bot-token path, so client_id/secret cannot
 * remove or shortcut the bot-token paste.
 */
function DiscordAppConnectPanel({
  agentUserId,
  agentConfigId,
  onChanged,
  connectNonce = 0,
  onForget,
  hasStoredCredentials = false,
}: {
  agentUserId: string;
  agentConfigId: string;
  onChanged: () => void;
  connectNonce?: number;
  onForget?: () => Promise<void>;
  hasStoredCredentials?: boolean;
}) {
  const t = useT();
  const [mode, setMode] = useState<'webhook' | 'bot'>('webhook');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [botToken, setBotToken] = useState('');
  const [interactionsUrl, setInteractionsUrl] = useState<string | null>(null);
  const [eventsUrl, setEventsUrl] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [forgetBusy, setForgetBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastConnectNonce = useRef(connectNonce);

  async function submit() {
    if (mode === 'webhook') {
      await submitWebhook();
      return;
    }
    if (!botToken.trim()) {
      setError('Paste the bot token first.');
      return;
    }
    setBusy(true);
    setError(null);
    setWarnings([]);
    try {
      const connectResponse = await fetch('/api/auth/connect/discord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          botToken: botToken.trim(),
          agentId: agentUserId,
          defaultInboundAgentId: agentUserId,
        }),
      });
      const connectData = await connectResponse.json().catch(() => ({}));
      if (!connectResponse.ok) {
        throw new Error(connectData.message ?? connectData.error ?? `discord_connect_${connectResponse.status}`);
      }

      const integrationResponse = await fetch(`/api/agents/${agentConfigId}/integrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'platform',
          provider: 'discord',
          enabledCapabilities: ['inbound_messages', 'send_messages', 'register_commands', 'post_in_channels'],
          isEnabled: true,
          connectedAccountId: typeof connectData.connectedAccountId === 'string'
            ? connectData.connectedAccountId
            : undefined,
          metadata: {
            applicationId: typeof connectData.applicationId === 'string' ? connectData.applicationId : undefined,
            defaultInboundAgentId: agentUserId,
          },
        }),
      });
      const integrationData = await integrationResponse.json().catch(() => ({}));
      if (!integrationResponse.ok) {
        throw new Error(friendlyConnectError(integrationData.error ?? `discord_integration_${integrationResponse.status}`));
      }

      setInteractionsUrl(typeof connectData.interactionsUrl === 'string' ? connectData.interactionsUrl : null);
      setEventsUrl(typeof connectData.eventsUrl === 'string' ? connectData.eventsUrl : null);
      setApplicationId(typeof connectData.applicationId === 'string' ? connectData.applicationId : null);
      setInstallUrl(typeof connectData.installUrl === 'string' ? connectData.installUrl : null);
      setWarnings(Array.isArray(connectData.warnings) ? connectData.warnings : []);
      setBotToken('');
      dispatchIntegrationChanged({ reason: 'connected_account_change', ref: 'discord', agentConfigId });
      dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: 'discord', agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to connect Discord bot');
    } finally {
      setBusy(false);
    }
  }

  async function submitWebhook() {
    if (!webhookUrl.trim()) {
      setError(t('agentTools.pasteDiscordWebhook'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const connectResponse = await fetch('/api/auth/connect/discord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webhookUrl: webhookUrl.trim(),
          agentId: agentUserId,
          defaultInboundAgentId: agentUserId,
        }),
      });
      const connectData = await connectResponse.json().catch(() => ({}));
      if (!connectResponse.ok) {
        throw new Error(connectData.message ?? connectData.error ?? `discord_webhook_${connectResponse.status}`);
      }

      const integrationResponse = await fetch(`/api/agents/${agentConfigId}/integrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'platform',
          provider: 'discord',
          enabledCapabilities: ['webhook_post'],
          isEnabled: true,
          connectedAccountId: typeof connectData.connectedAccountId === 'string'
            ? connectData.connectedAccountId
            : undefined,
          metadata: {
            credentialType: 'webhook',
            defaultInboundAgentId: agentUserId,
          },
        }),
      });
      const integrationData = await integrationResponse.json().catch(() => ({}));
      if (!integrationResponse.ok) {
        throw new Error(friendlyConnectError(integrationData.error ?? `discord_integration_${integrationResponse.status}`));
      }

      setWebhookUrl('');
      dispatchIntegrationChanged({ reason: 'connected_account_change', ref: 'discord', agentConfigId });
      dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: 'discord', agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to connect Discord webhook');
    } finally {
      setBusy(false);
    }
  }

  async function handleForget() {
    if (!onForget) return;
    setForgetBusy(true);
    setError(null);
    try {
      await onForget();
      setApplicationId(null);
      setInteractionsUrl(null);
      setEventsUrl(null);
      setInstallUrl(null);
      setWarnings([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to forget stored credentials');
    } finally {
      setForgetBusy(false);
    }
  }

  useEffect(() => {
    if (connectNonce === lastConnectNonce.current) return;
    lastConnectNonce.current = connectNonce;
    submit();
    // submit intentionally reads current Discord credential fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectNonce]);

  const ctaDisabled = busy || (mode === 'webhook' ? !webhookUrl.trim() : !botToken.trim());
  const ctaLabel = mode === 'webhook' ? t('agentTools.connectDiscordWebhook') : t('agentTools.connectDiscordBot');

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <p className="text-xs text-muted-foreground">
        {t('agentTools.discordZeroSetupPre')}{' '}
        <a
          href="/discord"
          target="_blank"
          rel="noreferrer noopener"
          className="underline hover:text-foreground"
        >
          @robutler.discord
        </a>{' '}
        {t('agentTools.discordZeroSetupPost')}
      </p>

      <div className="grid grid-cols-2 gap-1 rounded-md bg-muted/30 p-1">
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${mode === 'webhook' ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground'}`}
          onClick={() => {
            setMode('webhook');
            setError(null);
          }}
        >
          Webhook
        </button>
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${mode === 'bot' ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground'}`}
          onClick={() => {
            setMode('bot');
            setError(null);
          }}
        >
          Bot
        </button>
      </div>

      {mode === 'webhook' ? (
        <>
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('agentTools.discordWebhookUrl')}</label>
            <input
              className="input w-full text-sm"
              type="password"
              value={webhookUrl}
              onChange={(event) => {
                setWebhookUrl(event.target.value);
                setError(null);
              }}
              placeholder="https://discord.com/api/webhooks/..."
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('agentTools.discordWebhookHelpPre')}
            <code className="mx-1 rounded bg-muted/60 px-1">discord_send_webhook</code>
            {t('agentTools.discordWebhookHelpPost')}
          </p>
          <p className="text-xs text-muted-foreground">
            <a
              href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
              target="_blank"
              rel="noreferrer noopener"
              className="underline hover:text-foreground"
            >
              {t('agentTools.discordIntroWebhooks')} ↗
            </a>
          </p>
        </>
      ) : (
        <>
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('agentTools.botToken')}</label>
            <input
              className="input w-full text-sm"
              type="password"
              value={botToken}
              onChange={(event) => {
                setBotToken(event.target.value);
                setError(null);
              }}
              placeholder={t('agentTools.botTokenPlaceholder')}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            We derive the Application ID and public key from the bot token, push your agent&apos;s
            avatar and username onto the Discord bot, and configure the App&apos;s Interactions and
            Events endpoints.
          </p>
          {applicationId ? (
            <div className="space-y-1 text-xs">
              <div className="font-medium text-muted-foreground">{t('agentTools.openInDeveloperPortal')}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <a
                  href={`https://discord.com/developers/applications/${applicationId}/information`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  General ↗
                </a>
                <a
                  href={`https://discord.com/developers/applications/${applicationId}/bot`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  Bot ↗
                </a>
                <a
                  href={`https://discord.com/developers/applications/${applicationId}/oauth2`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  OAuth2 ↗
                </a>
                <a
                  href={`https://discord.com/developers/applications/${applicationId}/installation`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  Installation ↗
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-xs">
              <div className="font-medium text-muted-foreground">{t('agentTools.firstTimeOpenDiscord')}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <a
                  href="https://discord.com/developers/applications"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  {t('agentTools.developerPortal')} ↗
                </a>
                <a
                  href="https://discord.com/developers/docs/quick-start/getting-started#step-1-creating-an-app"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  {t('agentTools.howToAddBot')} ↗
                </a>
                <a
                  href="https://discord.com/developers/docs/topics/oauth2#bot-vs-user-accounts"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-foreground"
                >
                  {t('agentTools.resetBotToken')} ↗
                </a>
              </div>
            </div>
          )}
        </>
      )}

      {installUrl && (
        <div className="space-y-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs">
          <div className="font-medium text-emerald-700 dark:text-emerald-400">
            {t('agentTools.botReady')}
          </div>
          <p className="text-muted-foreground">
            {t('agentTools.botInstallHelp')}
          </p>
          <a
            href={installUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="btn-primary inline-flex items-center justify-center gap-2 text-xs"
          >
            {t('agentTools.addToDiscord')} ↗
          </a>
        </div>
      )}

      {(interactionsUrl || eventsUrl) && (
        <div className="space-y-2 rounded-md border border-border bg-muted/20 p-2 text-xs">
          {interactionsUrl && (
            <div>
              <div className="font-medium">{t('agentTools.interactionsEndpointUrl')}</div>
              <code className="block break-all text-muted-foreground">{interactionsUrl}</code>
            </div>
          )}
          {eventsUrl && (
            <div>
              <div className="font-medium">{t('agentTools.eventsUrl')}</div>
              <code className="block break-all text-muted-foreground">{eventsUrl}</code>
            </div>
          )}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-400">
          <div className="font-medium">{t('agentTools.connectedWithWarnings')}</div>
          <ul className="list-disc pl-4">
            {warnings.map((w) => (
              <li key={w}>{discordWarningLabel(w)}</li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}

      <button
        type="button"
        className="btn-primary w-full text-sm"
        disabled={ctaDisabled}
        onClick={submit}
      >
        {busy ? (
          <span className="inline-flex items-center gap-2">
            <Spinner size="sm" />
            <span>Connecting...</span>
          </span>
        ) : (
          ctaLabel
        )}
      </button>

      {hasStoredCredentials && onForget && (
        <button
          type="button"
          className="block w-full text-center text-xs text-red-500 underline-offset-2 hover:underline disabled:opacity-50"
          disabled={forgetBusy}
          onClick={handleForget}
        >
          {forgetBusy ? t('agentTools.forgetting') : t('agentTools.forgetStoredCredentials')}
        </button>
      )}
    </div>
  );
}

function friendlyConnectError(code: string): string {
  switch (code) {
    case 'app_already_bound_to_other_agent':
      return 'This bot is already connected to another agent. Disconnect it from that agent first or create a new bot.';
    case 'invalid_bot_token':
      return 'Bot token is invalid or has been revoked.';
    case 'token_is_not_bot_token':
      return 'This token belongs to a user, not a bot. Reset the bot token in the Discord Developer Portal (Bot → Reset Token).';
    case 'discord_unreachable':
      return 'Could not reach Discord. Try again in a moment.';
    case 'connected_account_not_owned':
      return 'This bot is owned by a different user.';
    default:
      return code;
  }
}

function discordWarningLabel(code: string): string {
  switch (code) {
    case 'username_rate_limited':
      return 'Bot username could not be updated (Discord rate-limits ~2 changes per hour). Avatar and other identity fields were synced.';
    case 'avatar_fetch_failed':
      return 'Could not fetch the agent avatar; Discord bot avatar was left unchanged.';
    case 'endpoints_patch_failed':
      return 'Could not configure App Interactions/Events endpoints. Set them manually in the Discord Developer Portal.';
    case 'identity_push_failed':
      return 'Discord rejected the identity update. The bot is connected; try renaming the agent later.';
    default:
      return code;
  }
}

function TelegramBotConnectPanel({
  agentUserId,
  agentConfigId,
  onChanged,
  connectNonce = 0,
  onForget,
  hasStoredCredentials = false,
}: {
  agentUserId: string;
  agentConfigId: string;
  onChanged: () => void;
  connectNonce?: number;
  onForget?: () => Promise<void>;
  hasStoredCredentials?: boolean;
}) {
  const t = useT();
  const [botToken, setBotToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgetBusy, setForgetBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastConnectNonce = useRef(connectNonce);

  async function submit() {
    if (!botToken.trim()) {
      setError(t('agentTools.pasteBotFatherToken'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const connectResponse = await fetch('/api/auth/connect/telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          botToken: botToken.trim(),
          agentId: agentUserId,
          defaultInboundAgentId: agentUserId,
        }),
      });
      const connectData = await connectResponse.json().catch(() => ({}));
      if (!connectResponse.ok) {
        throw new Error(connectData.message ?? connectData.error ?? `telegram_connect_${connectResponse.status}`);
      }
      if (typeof connectData.connectedAccountId !== 'string') {
        throw new Error('Telegram connected, but no account id was returned.');
      }

      const integrationResponse = await fetch(`/api/agents/${agentConfigId}/integrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'platform',
          provider: 'telegram',
          isEnabled: true,
          enabledCapabilities: ['inbound_messages', 'send_messages'],
          connectedAccountId: connectData.connectedAccountId,
          metadata: {
            botId: String(connectData.bot?.id ?? ''),
            // contact_pays is the platform default — the contact uses
            // their own welcome demo grant + linked balance. Owners
            // who want to fully sponsor their users can flip this in
            // the integration settings panel.
            billingMode: 'contact_pays',
          },
        }),
      });
      const integrationData = await integrationResponse.json().catch(() => ({}));
      if (!integrationResponse.ok) {
        throw new Error(friendlyConnectError(integrationData.error ?? `telegram_bind_${integrationResponse.status}`));
      }

      dispatchIntegrationChanged({ reason: 'connected_account_change', ref: 'telegram', agentConfigId });
      dispatchIntegrationChanged({ reason: 'platform_capability_change', ref: 'telegram', agentConfigId });
      setBotToken('');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to connect Telegram bot');
    } finally {
      setBusy(false);
    }
  }

  async function handleForget() {
    if (!onForget) return;
    setForgetBusy(true);
    setError(null);
    try {
      await onForget();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to forget stored credentials');
    } finally {
      setForgetBusy(false);
    }
  }

  useEffect(() => {
    if (connectNonce === lastConnectNonce.current) return;
    lastConnectNonce.current = connectNonce;
    submit();
    // submit intentionally reads the current bot token field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectNonce]);

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <div className="space-y-1">
        <label className="text-xs font-medium">{t('agentTools.botFatherToken')}</label>
        <input
          type="password"
          value={botToken}
          onChange={(event) => {
            setBotToken(event.target.value);
            setError(null);
          }}
          placeholder="1234567890:AA..."
          className="input w-full text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Create or select a bot in Telegram with @BotFather, paste its API token here. Incoming
          messages to that bot will be routed to this agent. We&apos;ll also push the agent&apos;s
          name + description onto the bot via the Bot API on connect.
        </p>
        <p className="text-xs text-muted-foreground">
          <a
            href="https://t.me/BotFather"
            target="_blank"
            rel="noreferrer noopener"
            className="underline hover:text-foreground"
          >
            {t('agentTools.openBotFather')} ↗
          </a>
          <span className="mx-2">•</span>
          <a
            href="https://core.telegram.org/bots/features#botfather"
            target="_blank"
            rel="noreferrer noopener"
            className="underline hover:text-foreground"
          >
            {t('agentTools.botFatherGuide')} ↗
          </a>
        </p>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}

      <button
        type="button"
        className="btn-primary w-full text-sm"
        disabled={busy || !botToken.trim()}
        onClick={submit}
      >
        {busy ? (
          <span className="inline-flex items-center gap-2">
            <Spinner size="sm" />
            <span>Connecting...</span>
          </span>
        ) : (
          'Connect Telegram bot'
        )}
      </button>

      {hasStoredCredentials && onForget && (
        <button
          type="button"
          className="block w-full text-center text-xs text-red-500 underline-offset-2 hover:underline disabled:opacity-50"
          disabled={forgetBusy}
          onClick={handleForget}
        >
          {forgetBusy ? t('agentTools.forgetting') : t('agentTools.forgetStoredCredentials')}
        </button>
      )}
    </div>
  );
}

function CustomMcpAddPanel({
  agentUserId,
  agentConfigId,
  onChanged,
  connectNonce = 0,
}: {
  agentUserId: string;
  agentConfigId: string;
  onChanged: () => void;
  connectNonce?: number;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [transport, setTransport] = useState<'http' | 'sse' | 'auto'>('http');
  const [authType, setAuthType] = useState<'none' | 'api_key' | 'oauth2'>('none');
  const [authHeaderName, setAuthHeaderName] = useState('x-api-key');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastConnectNonce = useRef(connectNonce);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/agents/${agentUserId}/mcp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          url,
          transport,
          authType,
          authHeaderName: authType === 'api_key' ? authHeaderName.trim() : undefined,
          apiKey: apiKey || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? `save_${res.status}`);
      dispatchIntegrationChanged({ reason: 'mcp_save', ref: name, agentConfigId });
      onChanged();
      if (authType === 'oauth2') {
        await startFeaturedMcpOauthFlow({
          catalog: { id: name, mcpUrl: url, transport },
          agentUserId,
        });
        return;
      }
      setName('');
      setUrl('');
      setTransport('http');
      setApiKey('');
      setAuthHeaderName('x-api-key');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add MCP server');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (connectNonce === lastConnectNonce.current) return;
    lastConnectNonce.current = connectNonce;
    submit();
    // submit intentionally reads current custom MCP form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectNonce]);

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <input className="input w-full text-sm" placeholder={t('agentTools.serverNamePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
      <input className="input w-full text-sm" placeholder="https://example.com/mcp" value={url} onChange={(e) => setUrl(e.target.value)} />
      <select
        className="input w-full text-sm"
        value={transport}
        onChange={(e) => {
          setTransport(e.target.value as 'http' | 'sse' | 'auto');
          setError(null);
        }}
      >
        <option value="http">{t('agentTools.streamableHttp')}</option>
        <option value="sse">SSE</option>
        <option value="auto">{t('agentTools.autoDetect')}</option>
      </select>
      <select className="input w-full text-sm" value={authType} onChange={(e) => setAuthType(e.target.value as 'none' | 'api_key' | 'oauth2')}>
        <option value="none">{t('agentTools.noAuth')}</option>
        <option value="api_key">{t('settings.authApiKey')}</option>
        <option value="oauth2">OAuth</option>
      </select>
      {authType === 'api_key' && (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)]">
          <input
            className="input w-full text-sm"
            placeholder={t('agentTools.headerNamePlaceholder')}
            value={authHeaderName}
            onChange={(e) => {
              setAuthHeaderName(e.target.value);
              setError(null);
            }}
          />
          <input
            className="input w-full text-sm"
            type="password"
            placeholder={t('agentTools.headerValuePlaceholder')}
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setError(null);
            }}
          />
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button type="button" className="btn-primary w-full text-sm" disabled={saving || !name || !url || (authType === 'api_key' && (!authHeaderName.trim() || !apiKey))} onClick={submit}>
        {saving ? <Spinner size="sm" /> : 'Add MCP server'}
      </button>
    </div>
  );
}

function CustomOpenApiAddPanel({
  agentUserId,
  agentConfigId,
  onChanged,
  connectNonce = 0,
}: {
  agentUserId: string;
  agentConfigId: string;
  onChanged: () => void;
  connectNonce?: number;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [specUrl, setSpecUrl] = useState('');
  const [authType, setAuthType] = useState<'none' | 'api_key' | 'oauth2'>('none');
  const [authHeaderName, setAuthHeaderName] = useState('x-api-key');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastConnectNonce = useRef(connectNonce);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/agents/${agentUserId}/openapi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          specUrl,
          authType,
          authHeaderName: authType === 'api_key' ? authHeaderName.trim() : undefined,
          apiKey: authType === 'none' ? undefined : apiKey || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? `save_${res.status}`);
      dispatchIntegrationChanged({ reason: 'openapi_save', ref: name, agentConfigId });
      onChanged();
      setName('');
      setSpecUrl('');
      setApiKey('');
      setAuthHeaderName('x-api-key');
      setAuthType('none');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add OpenAPI server');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (connectNonce === lastConnectNonce.current) return;
    lastConnectNonce.current = connectNonce;
    submit();
    // submit intentionally reads current custom OpenAPI form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectNonce]);

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <input className="input w-full text-sm" placeholder={t('agentTools.apiNamePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
      <input className="input w-full text-sm" placeholder="https://api.example.com/openapi.json" value={specUrl} onChange={(e) => setSpecUrl(e.target.value)} />
      <select
        className="input w-full text-sm"
        value={authType}
        onChange={(e) => {
          setAuthType(e.target.value as 'none' | 'api_key' | 'oauth2');
          setError(null);
        }}
      >
        <option value="none">{t('agentTools.noAuth')}</option>
        <option value="api_key">{t('settings.authApiKey')}</option>
        <option value="oauth2">{t('agentTools.oauthBearerToken')}</option>
      </select>
      {authType !== 'none' && (
        <input
          className="input w-full text-sm"
          type="password"
          placeholder={authType === 'oauth2' ? t('agentTools.bearerToken') : t('settings.authApiKey')}
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            setError(null);
          }}
        />
      )}
      {authType === 'api_key' && (
        <input
          className="input w-full text-sm"
          placeholder={t('agentTools.headerNamePlaceholder')}
          value={authHeaderName}
          onChange={(e) => {
            setAuthHeaderName(e.target.value);
            setError(null);
          }}
        />
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button
        type="button"
        className="btn-primary w-full text-sm"
        disabled={saving || !name || !specUrl || (authType !== 'none' && !apiKey) || (authType === 'api_key' && !authHeaderName.trim())}
        onClick={submit}
      >
        {saving ? <Spinner size="sm" /> : 'Add OpenAPI server'}
      </button>
    </div>
  );
}
